import React, { useContext, useEffect, useRef, useState } from "react";
import isNativeApp from "../../utils/isNativeApp";

import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import {
	History as HistoryIcon,
	GetApp as DownloadIcon,
	Refresh as RefreshIcon,
	AttachMoney as PaymentIcon,
	EventAvailable as EventAvailableIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import { ListItemIcon, ListItemText, Divider } from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import MergeBackupModal from "../MergeBackupModal";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";

import ScheduleModal from "../ScheduleModal";
import RequestPaymentDialog from "../RequestPaymentDialog";
import formatContactIdentifier from "../../helpers/formatContactIdentifier";

const TicketOptionsMenu = ({ ticket, menuOpen, handleClose, anchorEl, showTabGroups }) => {
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [mergeBackupModalOpen, setMergeBackupModalOpen] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [resettingChatbot, setResettingChatbot] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
	const [contactId, setContactId] = useState(null);
	const [requestPaymentOpen, setRequestPaymentOpen] = useState(false);
	const [triggeringAppointment, setTriggeringAppointment] = useState(false);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenTransferModal = e => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	const handleOpenScheduleModal = () => {
		handleClose();
		setContactId(ticket.contact.id);
		setScheduleModalOpen(true);
	}

	const handleCloseScheduleModal = () => {
		setScheduleModalOpen(false);
		setContactId(null);
	}

	const handleOpenMergeBackupModal = () => {
		handleClose();
		setMergeBackupModalOpen(true);
	}

	const handleCloseMergeBackupModal = () => {
		setMergeBackupModalOpen(false);
	}

	const handleScheduleAppointment = async () => {
		handleClose();
		setTriggeringAppointment(true);
		try {
			await api.post("/appointments/trigger", { ticketId: ticket.id });
			toast.success(i18n.t("ticketOptionsMenu.appointmentTriggered"));
		} catch (err) {
			toastError(err);
		} finally {
			setTriggeringAppointment(false);
		}
	};

	// Reset chatbot handler - triggers fresh bot flow
	const handleResetChatbot = async () => {
		handleClose();
		setResettingChatbot(true);
		
		try {
			await api.put(`/tickets/${ticket.id}`, {
				chatbot: true,
				queueOptionId: null,
				queueId: null,
			});
			// The socket event will update the ticket state
		} catch (err) {
			toastError(err);
		} finally {
			setResettingChatbot(false);
		}
	};

	// Download conversation handler
	const handleDownloadConversation = async () => {
		handleClose();
		setDownloading(true);
		
		try {
			// Fetch all messages for this ticket
			const { data } = await api.get(`/messages/${ticket.id}`, {
				params: { pageNumber: 1, limit: 10000 } // Get all messages
			});
			
			const messages = data.messages || [];
			const contact = ticket.contact;
			// For a BSUID (username-only) contact, `number` holds a synthetic
			// wau_ key — never write it out; use the @username/friendly label.
			const phoneNumber = formatContactIdentifier(contact).display || "unknown";
			const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
			
			// Format conversation as text
			let content = `═══════════════════════════════════════════════════════════\n`;
			content += `                    CONVERSATION EXPORT\n`;
			content += `═══════════════════════════════════════════════════════════\n\n`;
			content += `Contact: ${contact?.name || "Unknown"}\n`;
			content += `Phone: ${phoneNumber}\n`;
			content += `Ticket #: ${ticket.id}\n`;
			content += `Status: ${ticket.status}\n`;
			content += `Queue: ${ticket.queue?.name || "N/A"}\n`;
			content += `Export Date: ${format(new Date(), "MMMM d, yyyy HH:mm")}\n`;
			content += `Total Messages: ${messages.length}\n\n`;
			content += `───────────────────────────────────────────────────────────\n\n`;
			
			let currentDate = null;
			let mediaFiles = []; // Collect media files
			
			messages.forEach((msg) => {
				const msgDate = format(parseISO(msg.createdAt), "yyyy-MM-dd");
				const msgTime = format(parseISO(msg.createdAt), "HH:mm");
				
				// Add date separator if date changed
				if (msgDate !== currentDate) {
					currentDate = msgDate;
					content += `\n─── ${format(parseISO(msg.createdAt), "MMMM d, yyyy")} ───\n\n`;
				}
				
				const sender = msg.fromMe ? "Agent" : (contact?.name || "Customer");
				
				// Handle media files
				let mediaInfo = "";
				if (msg.mediaUrl) {
					const mediaType = msg.mediaType || "file";
					const mediaUrl = msg.mediaUrl.startsWith("http") 
						? msg.mediaUrl 
						: `${backendUrl}${msg.mediaUrl}`;
					mediaInfo = `\n   📎 [${mediaType.toUpperCase()}]: ${mediaUrl}`;
					mediaFiles.push({
						type: mediaType,
						url: mediaUrl,
						timestamp: msgTime,
						date: msgDate
					});
				}
				
				content += `[${msgTime}] ${sender}:\n`;
				content += `${msg.body || "(No text)"}${mediaInfo}\n\n`;
			});
			
			// Add media files summary at the end
			if (mediaFiles.length > 0) {
				content += `\n═══════════════════════════════════════════════════════════\n`;
				content += `                    MEDIA FILES (${mediaFiles.length})\n`;
				content += `═══════════════════════════════════════════════════════════\n\n`;
				
				mediaFiles.forEach((media, index) => {
					content += `${index + 1}. [${media.date} ${media.timestamp}] ${media.type.toUpperCase()}\n`;
					content += `   ${media.url}\n\n`;
				});
			}
			
			content += `\n═══════════════════════════════════════════════════════════\n`;
			content += `                      END OF CONVERSATION\n`;
			content += `═══════════════════════════════════════════════════════════\n`;
			
			// Create and download file - use phone number in filename
			const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `${phoneNumber}_${format(new Date(), "yyyyMMdd_HHmm")}.txt`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
			
		} catch (err) {
			toastError(err);
		} finally {
			setDownloading(false);
		}
	};

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>
				<MenuItem onClick={handleOpenScheduleModal}>
					{i18n.t("ticketOptionsMenu.schedule")}
				</MenuItem>
				<MenuItem onClick={handleOpenTransferModal}>
					{i18n.t("ticketOptionsMenu.transfer")}
				</MenuItem>
				<Can
					role={user.profile}
					perform="ticket-options:scheduleAppointment"
					yes={() => (
						<MenuItem
							onClick={handleScheduleAppointment}
							disabled={triggeringAppointment}
						>
							<ListItemIcon style={{ minWidth: 36 }}>
								<EventAvailableIcon
									fontSize="small"
									style={{ color: "#128c7e" }}
								/>
							</ListItemIcon>
							<ListItemText
								primary={
									triggeringAppointment
										? i18n.t("ticketOptionsMenu.appointmentTriggering")
										: i18n.t("ticketOptionsMenu.scheduleAppointment")
								}
							/>
						</MenuItem>
					)}
				/>
				<MenuItem onClick={handleResetChatbot} disabled={resettingChatbot}>
					<ListItemIcon style={{ minWidth: 36 }}>
						<RefreshIcon fontSize="small" style={{ color: "#9c27b0" }} />
					</ListItemIcon>
					<ListItemText primary={i18n.t("ticketOptionsMenu.resetChatbot") || "🔄 Reset Chatbot"} />
				</MenuItem>
				{/* Apple 3.1.1: hide "Request payment" menu item in native app —
					the dialog generates a Tranzila link the agent sends to the
					customer. Apple flags any "payment" UI inside the app. */}
				{!isNativeApp() && (
					<MenuItem
						onClick={() => {
							handleClose();
							setRequestPaymentOpen(true);
						}}
					>
						<ListItemIcon style={{ minWidth: 36 }}>
							<PaymentIcon fontSize="small" style={{ color: "#10b981" }} />
						</ListItemIcon>
						<ListItemText
							primary={
								i18n.t("ticketOptionsMenu.requestPayment") || "Request payment"
							}
						/>
					</MenuItem>
				)}
				<MenuItem onClick={handleOpenMergeBackupModal}>
					<ListItemIcon style={{ minWidth: 36 }}>
						<HistoryIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={i18n.t("ticketOptionsMenu.mergeBackup")} />
				</MenuItem>
				<MenuItem onClick={handleDownloadConversation} disabled={downloading}>
					<ListItemIcon style={{ minWidth: 36 }}>
						<DownloadIcon fontSize="small" style={{ color: "#607d8b" }} />
					</ListItemIcon>
					<ListItemText primary={downloading ? i18n.t("ticketOptionsMenu.downloading") || "Downloading..." : i18n.t("ticketOptionsMenu.downloadConversation") || "📥 Download Conversation"} />
				</MenuItem>

				<Divider style={{ margin: "8px 0" }} />
				
				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<MenuItem onClick={handleOpenConfirmationModal}>
							{i18n.t("ticketOptionsMenu.delete")}
						</MenuItem>
					)}
				/>
			</Menu>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
					ticket.contact.name
				}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<TransferTicketModalCustom
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
			/>
			<ScheduleModal
				open={scheduleModalOpen}
				onClose={handleCloseScheduleModal}
				aria-labelledby="form-dialog-title"
				contactId={contactId}
			/>
			<MergeBackupModal
				open={mergeBackupModalOpen}
				onClose={handleCloseMergeBackupModal}
				ticket={ticket}
			/>
			<RequestPaymentDialog
				open={requestPaymentOpen}
				onClose={() => setRequestPaymentOpen(false)}
				ticket={ticket}
			/>
		</>
	);
};

export default TicketOptionsMenu;
